import type { BriefProgressEvent } from "@/lib/world-helpers";
import type { CachedImage } from "@/lib/reference-image-cache";
import type { MediaDraft } from "@/lib/media-drafts";

export interface MediaItem {
  id: string;
  image_url: string;
  title: string;
  type: string;
}

export type SidebarTab = "library" | "references" | "research" | "art" | "draw" | "generate" | "drafts";

export const IMAGE_MODELS = [
  { value: "google/gemini-2.5-flash-image", label: "Flash (Fast)", description: "Fast generation, good quality" },
  { value: "google/gemini-3.1-flash-image-preview", label: "Flash Pro (Balanced)", description: "Pro quality at flash speed" },
  { value: "google/gemini-3-pro-image-preview", label: "Pro (Best)", description: "Highest quality, slower" },
] as const;

/** Type-specific default prompts */
export const defaultPromptForType: Record<string, string> = {
  banner: "A sweeping, panoramic vista that captures the essence and atmosphere. Wide cinematic composition suitable as a banner header image.",
  icon: "A clean, iconic symbol or emblem. Centered, simple, bold design suitable as a small square icon. On a clean background.",
  portrait: "A detailed character portrait. Expressive, cinematic lighting, atmospheric backdrop.",
  scene: "An atmospheric scene with rich environmental detail, cinematic composition.",
  map: "A top-down cartographic map view with varied terrain, geographic features, and landscapes. No text, labels, or typography of any kind.",
  card: "A painterly illustration suitable for a card frame. Centered subject, rich detail, vibrant colors.",
};

/** Image type context injected into briefs */
export const imageTypeContext: Record<string, string> = {
  banner: "IMAGE PURPOSE: Generate a BANNER image — a wide, panoramic, atmospheric header image. Do NOT generate a character portrait.",
  icon: "IMAGE PURPOSE: Generate an ICON — a small, clean, symbolic emblem or logo. Do NOT generate a full scene or portrait.",
  portrait: "IMAGE PURPOSE: Generate a CHARACTER PORTRAIT — focus on the person/creature's face and upper body.",
  scene: "IMAGE PURPOSE: Generate a SCENE — an atmospheric, environmental wide shot of the location or event.",
  map: "IMAGE PURPOSE: Generate a MAP — a top-down cartographic view showing terrain, landscapes, and geographic features. This will be used as an interactive map background where location markers will be overlaid. Do NOT include any text, labels, names, legends, compass labels, or typography whatsoever. The image must be PURELY VISUAL terrain and geography with zero readable text. Focus on geographic diversity: mountains, forests, rivers, coastlines, deserts, plains.",
  card: "IMAGE PURPOSE: Generate a CARD illustration — centered subject with rich detail for a card frame.",
};

/** Shared state interface passed to tab components */
export interface ImageGenTabProps {
  entity: {
    id: string;
    name: string;
    type: string;
    description: string;
    world_id: string;
    image_url?: string | null;
  };
  worldId: string;
  imageType: string;
  imageLabel: string;

  // Brief & search state
  briefText: string;
  setBriefText: (v: string) => void;
  hasCachedBrief: boolean;
  setHasCachedBrief: (v: boolean) => void;
  briefLoading: boolean;
  generating: boolean;

  // Web search
  webSearchEnabled: boolean;
  setWebSearchEnabled: (v: boolean) => void;
  webSearchKeywords: string;
  setWebSearchKeywords: (v: string) => void;
  webReferenceImages: string[];
  webReferenceThumbnails: string[];
  searchQueriesUsed: string[];

  // Codex
  codexSearchEnabled: boolean;
  setCodexSearchEnabled: (v: boolean) => void;
  codexSearchKeywords: string;
  setCodexSearchKeywords: (v: string) => void;
  codexPrompt: string;
  setCodexPrompt: (v: string) => void;
  codexSnippets: string[];
  codexEntityImages: string[];

  // Art direction
  generationPrompt: string;
  setGenerationPrompt: (v: string) => void;
  selectedModel: string;
  setSelectedModel: (v: string) => void;

  // Seed / references
  seedImageUrl: string | null;
  setSeedImageUrl: (v: string | null) => void;
  selectedRefImages: Set<string>;
  toggleRefImage: (url: string) => void;
  creativityLevel: number;
  setCreativityLevel: (v: number) => void;
  drawingDataUrl: string | null;
  setDrawingDataUrl: (v: string | null) => void;

  // Generated
  generatedPreview: string | null;
  setGeneratedPreview: (v: string | null) => void;
  generationStep: string | null;
  generationError: string | null;

  // Step toggles
  stepReferencesEnabled: boolean;
  stepResearchEnabled: boolean;
  stepArtEnabled: boolean;
  stepDrawEnabled: boolean;

  // Actions
  fetchBrief: (useWebSearch?: boolean, isSearchMore?: boolean) => Promise<void>;
  handleGenerate: () => Promise<void>;
  handleFileUpload: (file: File) => Promise<void>;
  handleSeedUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // Existing media
  existingMedia?: MediaItem[];
  currentImageUrl?: string | null;
  onImageAccepted?: (imageUrl: string) => void;
  onImageGenerated?: (entityId: string, imageUrl: string) => void;
  onImageRemoved?: () => void;
  onOpenChange: (open: boolean) => void;

  // Dialog state
  dialogTitle?: string;
  previewAspectRatio?: string;
  uploadingSeed: boolean;
  showMediaPicker: boolean;
  setShowMediaPicker: (v: boolean) => void;
  setActiveTab: (tab: SidebarTab) => void;

  // Drawing
  drawingInitialImage: string | null;
  setDrawingInitialImage: (v: string | null) => void;
  saveDrawingToWorldMedia: (dataUrl: string) => Promise<void>;

  // Debug
  isAdminUser: boolean;
  debugData: any;
  setShowDebugDialog: (v: boolean) => void;
  searchProgress: BriefProgressEvent | null;

  // Zoom
  setZoomRefImage: (v: string | null) => void;

  // Cache
  cachedImages: CachedImage[];
  setShowCachePopup: (v: boolean) => void;

  // Drafts
  drafts: MediaDraft[];
  currentDraftId: string | null;
  savingDraft: boolean;
  loadDraft: (draft: MediaDraft) => void;
  handleDeleteDraft: (id: string) => Promise<void>;

  // Render helpers
  renderRefImageGrid: (images: string[], thumbnails: string[], sourceBadge?: React.ReactNode) => React.ReactNode;
}
